import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { SheetsSaveError } from '../obr/sheetsApiStorage.ts';
import { broadcastCombatSync } from '../obr/combatSync.ts';

export interface CombatActionMarkResult {
  tracked: boolean;
  outOfActions: boolean;
  actionsApplied: number;
}

async function retryableMine(storage: SheetsApiStorage, characterId: string) {
  const combat = await storage.getCombat();
  const mine = combat?.participants.find((participant) => participant.mine && participant.characterId === characterId) ?? null;
  return { combat, mine };
}

/**
 * Reflect a successful sheet Action into the shared room tracker. This is
 * bookkeeping, not permission: the game action has already succeeded, so a
 * full tracker warns instead of retroactively undoing the roll/cast.
 */
export async function markCombatActions(
  storage: SheetsApiStorage,
  characterId: string,
  count: 1 | 2,
  reason: string,
): Promise<CombatActionMarkResult> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const { combat, mine } = await retryableMine(storage, characterId);
    if (!combat || !mine) return { tracked: false, outOfActions: false, actionsApplied: 0 };
    try {
      const result = await storage.spendCombatActions(combat.combatId, characterId, count, mine.version);
      if (result.actionsApplied > 0) await broadcastCombatSync(reason);
      return { tracked: true, outOfActions: result.outOfActions, actionsApplied: result.actionsApplied };
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale' && attempt === 0) continue;
      throw error;
    }
  }
  return { tracked: false, outOfActions: false, actionsApplied: 0 };
}

export interface CombatEvadeMarkResult {
  tracked: boolean;
  outOfActions: boolean;
  actionApplied: boolean;
  alreadyRecorded: boolean;
}

/** First Evade in a round pays one Action; later Evades do not pay again. */
export async function markCombatEvade(
  storage: SheetsApiStorage,
  characterId: string,
): Promise<CombatEvadeMarkResult> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const { combat, mine } = await retryableMine(storage, characterId);
    if (!combat || !mine) {
      return { tracked: false, outOfActions: false, actionApplied: false, alreadyRecorded: false };
    }
    try {
      const result = await storage.recordCombatEvade(combat.combatId, characterId, mine.version);
      if (result.actionApplied || !result.alreadyRecorded) await broadcastCombatSync('player-evade');
      return { tracked: true, ...result };
    } catch (error) {
      if (error instanceof SheetsSaveError && error.reason === 'combat-stale' && attempt === 0) continue;
      throw error;
    }
  }
  return { tracked: false, outOfActions: false, actionApplied: false, alreadyRecorded: false };
}
